import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  ViewContainerRef,
} from '@angular/core';
import { AngularComponentFactory, QuestionAngular } from 'survey-angular-ui';
import { Question, Serializer } from 'survey-core';

export interface PropertyTypologyGroupItem {
  value: number;
  text: string;
}

export interface PropertyTypologyGroup {
  value: number | string;
  label: string;
  items: PropertyTypologyGroupItem[];
}

export class QuestionPropertyTypologyModel extends Question {
  public override getType(): string {
    return 'property-typology';
  }

  public get placeholder(): string {
    return this.getPropertyValue('placeholder') || 'Tipo di immobile';
  }
  public set placeholder(val: string) {
    this.setPropertyValue('placeholder', val);
  }

  public get selectAllText(): string {
    return this.getPropertyValue('selectAllText') || 'Tutte le tipologie';
  }
  public set selectAllText(val: string) {
    this.setPropertyValue('selectAllText', val);
  }

  /** Testo del pulsante nel footer del dropdown. Stringa vuota => footer nascosto. */
  public get confirmText(): string {
    return this.getPropertyValue('confirmText') || '';
  }
  public set confirmText(val: string) {
    this.setPropertyValue('confirmText', val);
  }

  /**
   * Nome del campo del survey su cui scrivere i codici di tipologia selezionati.
   * Se vuoto i codici vengono scritti nel value della domanda.
   * Nota: non puo' chiamarsi `valueName` perche' e' gia' una proprieta' di survey-core.
   */
  public get targetName(): string {
    return this.getPropertyValue('targetName') || '';
  }
  public set targetName(val: string) {
    this.setPropertyValue('targetName', val);
  }

  private _groups?: PropertyTypologyGroup[];

  public get groups(): PropertyTypologyGroup[] {
    if (Array.isArray(this._groups) && this._groups.length > 0) {
      return this._groups;
    }
    const rawGroups = (this as any)['groups'];
    if (!Array.isArray(rawGroups)) {
      return [];
    }
    return rawGroups.map((g: any) => ({
      value: g?.value,
      label: g?.label ?? '',
      items: Array.isArray(g?.items) ? g.items : [],
    }));
  }
  public set groups(val: PropertyTypologyGroup[]) {
    this._groups = val;
  }

  public override isEmpty(): boolean {
    if (this.targetName && this.survey) {
      const surveyAny = this.survey as any;
      return surveyAny.getValue?.(this.targetName) == null;
    }
    return super.isEmpty();
  }
}

export function registerPropertyTypologyQuestion(): void {
  if (!Serializer.findClass('property-typology')) {
    Serializer.addClass(
      'property-typology',
      [
        { name: 'placeholder', default: 'Tipo di immobile' },
        { name: 'selectAllText', default: 'Tutte le tipologie' },
        { name: 'confirmText', default: '' },
        { name: 'targetName', default: '' },
        {
          name: 'groups',
          onSetValue: (obj: any, val: any) => {
            obj.groups = val;
          },
          onGetValue: (obj: any) => obj.groups,
        },
      ],
      function () {
        return new QuestionPropertyTypologyModel('');
      },
      'question'
    );
  }
  AngularComponentFactory.Instance.registerComponent(
    'property-typology-question',
    PropertyTypologyQuestionComponent
  );
}

@Component({
  selector: 'app-property-typology-question',
  templateUrl: './property-typology-question.component.html',
  styleUrls: ['./property-typology-question.component.scss'],
})
export class PropertyTypologyQuestionComponent extends QuestionAngular<QuestionPropertyTypologyModel> {
  public isOpen: boolean = false;
  public expanded: Record<string, boolean> = {};
  public selected: Set<number> = new Set<number>();

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    viewContainerRef: ViewContainerRef,
    private elementRef: ElementRef<HTMLElement>
  ) {
    super(changeDetectorRef, viewContainerRef);
  }

  public get groups(): PropertyTypologyGroup[] {
    return this.model?.groups || [];
  }

  public get hasSelected(): boolean {
    return this.selected.size > 0;
  }

  public get allItems(): PropertyTypologyGroupItem[] {
    const items: PropertyTypologyGroupItem[] = [];
    for (const group of this.groups) {
      for (const item of group.items) {
        items.push(item);
      }
    }
    return items;
  }

  public groupKey(group: PropertyTypologyGroup): string {
    return String(group.value);
  }

  public hasContent(group: PropertyTypologyGroup): boolean {
    return Array.isArray(group?.items) && group.items.length > 0;
  }

  public isExpanded(group: PropertyTypologyGroup): boolean {
    return !!this.expanded[this.groupKey(group)];
  }

  public isItemSelected(value: number | string): boolean {
    return this.selected.has(Number(value));
  }

  public isGroupSelected(group: PropertyTypologyGroup): boolean {
    if (!this.hasContent(group)) {
      return false;
    }
    return group.items.every(item => this.selected.has(Number(item.value)));
  }

  public displayText: string | null = null;

  public get controlText(): string {
    if (this.displayText !== null) {
      return this.displayText;
    }
    // Prima apertura (o r-render del componente): deriva dal value gia' presente
    const codes = this.sortSelection(Array.from(this.readSelection()));
    this.displayText = this.buildDisplayText(codes);
    return this.displayText || this.model?.placeholder || 'Tipo di immobile';
  }

  public toggleDropdown(event: Event): void {
    if (this.model?.isReadOnly) return;
    event.stopPropagation();
    if (this.isOpen) {
      this.onConfirm();
    } else {
      this.openDropdown();
    }
    this.detectChanges();
  }

  public openDropdown(): void {
    this.selected = this.readSelection();
    // Al primo apertura espande il primo gruppo con contenuto, come sul portale
    this.expanded = {};
    const firstWithContent = this.groups.find(g => this.hasContent(g));
    if (firstWithContent && this.selected.size === 0) {
      this.expanded[this.groupKey(firstWithContent)] = true;
    }
    this.isOpen = true;
    this.detectChanges();
  }

  public toggleGroup(group: PropertyTypologyGroup, event?: Event): void {
    event?.stopPropagation();
    if (!this.hasContent(group)) return;
    const isSelected = this.isGroupSelected(group);
    for (const item of group.items) {
      const value = Number(item.value);
      if (isSelected) {
        this.selected.delete(value);
      } else {
        this.selected.add(value);
      }
    }
    this.detectChanges();
  }

  public toggleItem(value: number | string, event?: Event): void {
    event?.stopPropagation();
    const num = Number(value);
    if (this.selected.has(num)) {
      this.selected.delete(num);
    } else {
      this.selected.add(num);
    }
    this.detectChanges();
  }

  public onGroupTitleClick(group: PropertyTypologyGroup, event: Event): void {
    if (this.model?.isReadOnly) return;
    event.stopPropagation();
    if (!this.hasContent(group)) {
      this.toggleGroup(group);
      this.detectChanges();
      return;
    }
    this.expanded[this.groupKey(group)] = !this.isExpanded(group);
    this.detectChanges();
  }

  public onGroupChevronClick(group: PropertyTypologyGroup, event: Event): void {
    event.stopPropagation();
    this.onGroupTitleClick(group, event);
  }

  public onConfirm(): void {
    const codes = this.sortSelection(Array.from(this.selected));

    if (this.model?.targetName && this.model.survey) {
      const surveyAny = this.model.survey as any;
      surveyAny.setValue?.(
        this.model.targetName,
        codes.length > 0 ? codes : undefined
      );
      // Evita la duplicazione dell'oggetto nell'outcome
      this.model.value = undefined;
    } else {
      this.model.value = codes.length > 0 ? codes : undefined;
    }

    this.displayText = this.buildDisplayText(codes);
    this.isOpen = false;
    this.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.onConfirm();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.detectChanges();
    }
  }

  /** Mantiene l'ordine di dichiarazione dei gruppi, indipendentemente dalla selezione. */
  private sortSelection(codes: number[]): number[] {
    const order: number[] = [];
    for (const item of this.allItems) {
      order.push(Number(item.value));
    }
    return codes
      .filter(code => order.includes(code))
      .sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  private readSelection(): Set<number> {
    const codes =
      this.model?.targetName && this.model?.survey
        ? this.readFromSurvey()
        : this.readFromValue();

    const known = new Set(this.allItems.map(i => Number(i.value)));
    return new Set(codes.filter(code => known.has(code)));
  }

  private readFromSurvey(): number[] {
    const surveyAny = this.model.survey as any;
    return this.toNumberArray(surveyAny.getValue?.(this.model.targetName));
  }

  private readFromValue(): number[] {
    return this.toNumberArray(this.model?.value);
  }

  private toNumberArray(value: any): number[] {
    if (value == null) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.map(Number).filter(n => !isNaN(n));
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed)
          ? parsed.map(Number).filter(n => !isNaN(n))
          : [];
      } catch {
        return [];
      }
    }
    const num = Number(value);
    return isNaN(num) ? [] : [num];
  }

  private buildDisplayText(codes: number[]): string {
    const labels = this.allItems
      .filter(item => codes.includes(Number(item.value)))
      .map(item => item.text);

    if (labels.length === 0) {
      return '';
    }
    if (labels.length === 1) {
      return labels[0];
    }
    if (labels.length <= 3) {
      return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
    }
    return `${labels.length} tipologie selezionate`;
  }
}

registerPropertyTypologyQuestion();
